/* eslint-disable local/require-isPending-for-mutations */
import { useApps } from '@auth/useApps'
import AppPaths from '@globals/paths'
import { tabletQuery } from '@globals/themes/themeConstants'
import { useApiActionToast } from '@globals/toaster/useToasterStore'
import {
  AwardType,
  AwardTypeEmbeds,
  createAwardType,
  CreateOrUpdateAwardTypeParams,
  getAwardType,
  initiateAwardUpload,
  MediaVariantEnum,
  updateAwardType,
} from '@kudos/http-client'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useApiError } from '@shared/errors/useApiError'
import { useInvalidateQuery } from '@shared/hooks/useInvalidateQuery'
import useMediaUrl from '@shared/hooks/useMediaUrl'
import { AppPermission } from '@shared/permissions'
import { getSendToFromTenantMemberAndTeams } from '@shared/utils/getSendToFromTenantMemberAndTeams'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useT } from '@transifex/react'
import { FieldValues, UseFormSetError } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { AwardTypeFormData } from './useConfigureAwardTypeForm'

export const AWARD_TYPE_QK = 'AWARD_TYPE_QUERY_KEY'

export const AWARD_EMBEDS: AwardTypeEmbeds[] = [
  'attachmentUrls',
  'pointsBankName',
  'pointsBankBalance',
  'senders',
  'stats',
]

const useAwardTypeDataAndMutations = (awardId: string | undefined, embeds: AwardTypeEmbeds[]) => {
  const awardTypeQueryKey = [AWARD_TYPE_QK, awardId]
  const isEdit = !!awardId
  const isTablet = useMediaQuery(tabletQuery)
  const [showSuccess] = useApiActionToast()
  const { handleApiError } = useApiError()
  const invalidateQuery = useInvalidateQuery()
  const t = useT()
  const navigate = useNavigate()
  const [hasAppPermission] = useApps()
  const canConfigurePoints = hasAppPermission(
    'pointsApp',
    AppPermission.PointsAppViewPointsConfiguration,
  )

  const { data: awardTypeData, isFetched: isAwardTypeFetched } = useQuery({
    queryKey: awardTypeQueryKey,
    queryFn: () => getAwardType(awardId ?? '', { embeds }),
    enabled: !!awardId,
  })

  const { mutateAsync: createOrUpdateAwardMutation } = useMutation({
    mutationFn: (params: CreateOrUpdateAwardTypeParams) => {
      return isEdit ? updateAwardType(awardId, params) : createAwardType(params)
    },
    onSuccess: async (data: AwardType) => {
      showSuccess(isEdit ? t('Award successfully updated') : t('Award successfully created'))
      await invalidateQuery(awardTypeQueryKey)
      navigate(AppPaths.tenant.admin.viewAwardType(data.id))
    },
    onError: error => handleApiError(error),
  })

  const initiateMediaUploadMutation = useMutation({
    mutationFn: ({
      originalFilename,
      contentType,
    }: {
      originalFilename: string
      contentType: string
    }) => initiateAwardUpload({ originalFilename, contentType }),
  })

  const { url: awardTypeMediaUrl } = useMediaUrl(
    awardTypeData?.attachmentUrls,
    isTablet ? MediaVariantEnum.AWARD_BADGE_MOBILE : MediaVariantEnum.AWARD_BADGE_DESKTOP,
  )

  const createOrUpdateAwardType = async (
    formData: AwardTypeFormData,
    mediaObjectId: string | null,
    setError: UseFormSetError<FieldValues>,
    reset: () => void,
  ) => {
    const payload: CreateOrUpdateAwardTypeParams = {
      name: formData.name,
      description: formData.description,
      points: Number.isInteger(formData.points) && canConfigurePoints ? formData.points : 0,
      senders: getSendToFromTenantMemberAndTeams(formData.senders),
      pointsLocked:
        Number(formData.points) === 0 || !canConfigurePoints ? true : formData.pointsLocked,
      budgetItemId:
        Number(formData.points) === 0 || !canConfigurePoints ? null : formData.budgetItemId || null,
      mediaObjectId: mediaObjectId,
      archived: !formData.archived,
      nomination: false,
    }

    await createOrUpdateAwardMutation(payload, {
      onSuccess: () => {
        reset()
      },
      onError: error =>
        handleApiError(error, setError, [
          'name',
          'points',
          'pointsLocked',
          'senders',
          'description',
          'archived',
          'budgetItemId',
        ]),
    })
  }

  return {
    awardTypeData,
    isAwardTypeFetched,
    awardTypeMediaUrl,
    createOrUpdateAwardType,
    initiateMediaUploadMutation,
  }
}

export default useAwardTypeDataAndMutations
